import { Box, Group, Stack, Title, useMantineTheme } from "@mantine/core"
import type { BoxProps } from "@mantine/core"
import {
  IconThumbUp,
  IconAlertTriangle,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react"
import { ActionIcon, Tooltip } from "@tfg/ui"
import { useState } from "react"
import type { ReactNode } from "react"

export type InlineBannerProps = {
  type: "error" | "info" | "success" | "admin"
  message?: string | ReactNode
  children?: ReactNode
  closable?: boolean
  size?: "xs" | "sm" | "md" | "lg" | string
  tooltip?: string
} & BoxProps

export function InlineBanner({
  type,
  message,
  children,
  closable = true,
  size = "sm",
  tooltip,
  ...props
}: InlineBannerProps) {
  const theme = useMantineTheme()
  const [open, setOpen] = useState<boolean>(true)

  const getTypeStyles = (): {
    readonly bgColor: string
    readonly color: string
    readonly closeColor: string
    readonly Icon: typeof IconInfoCircle
  } => {
    switch (type) {
      case "error":
        return {
          bgColor: "var(--mantine-color-red-0)",
          color: "var(--mantine-color-error-text)",
          closeColor: "var(--mantine-color-error-text)",
          Icon: IconAlertTriangle,
        }
      case "success":
        return {
          bgColor: "var(--tfg-bg-color-success)",
          color: "var(--mantine-color-success-text)",
          closeColor: "var(--mantine-color-success-text)",
          Icon: IconThumbUp,
        }
      case "info":
        return {
          bgColor: "var(--tfg-bg-color-notice)",
          color: "var(--icon-info-color)",
          closeColor: "var(--icon-info-color)",
          Icon: IconInfoCircle,
        }
      case "admin":
        return {
          bgColor: theme.colors.admin[0],
          color: theme.colors.admin[9],
          closeColor: theme.colors.admin[9],
          Icon: IconInfoCircle,
        }
      default:
        return {
          bgColor: theme.colors.gray[0],
          color: theme.colors.gray[9],
          closeColor: theme.colors.gray[9],
          Icon: IconInfoCircle,
        }
    }
  }

  const { bgColor, color, closeColor, Icon } = getTypeStyles()

  if (!open) {
    return null
  }

  return (
    <Box pos="relative" {...props}>
      {(() => {
        const stackContent = (
          <Stack
            mih={20}
            bg={bgColor}
            py="var(--tfg-molecule-padding-y)"
            px="var(--tfg-molecule-padding-x)"
            gap="var(--tfg-molecule-padding-y)"
            fz={size}
            color={color}
            style={{ borderRadius: "var(--tfg-card-border-radius)" }}
            justify="space-between"
          >
            {message ? (
              <Group wrap="nowrap" align="center" gap={"xs"}>
                <Icon size={12} color={color} />
                {typeof message === "string" ? (
                  <Title c={color} order={6} fz={size}>
                    {message}
                  </Title>
                ) : (
                  message
                )}
              </Group>
            ) : null}
            {closable ? (
              <ActionIcon
                aria-label="Close banner"
                icon={<IconX />}
                size={15}
                stroke={2}
                variant="ghost"
                c={closeColor}
                pos="absolute"
                right={"var(--mantine-spacing-xs)"}
                top="xs"
                onClick={() => setOpen(false)}
              />
            ) : null}
            {children}
          </Stack>
        )

        return tooltip ? (
          <Tooltip label={tooltip}>{stackContent}</Tooltip>
        ) : (
          stackContent
        )
      })()}
    </Box>
  )
}
